/* DRIVE IQ — ADVENTURE MODE V600.29 MEDIA FALLBACK AUTHORITY
   File: adventure-photo.js

   V600.29 focused repair:
   - Paints a category-aware branded fallback immediately for every hero and shelf thumbnail.
   - Never allows a photo element to remain as a black or empty square while remote media resolves.
   - Distinguishes a deliberate fallback from a successfully resolved photo so fallback artwork never blocks a later real image.
   - Restores the fallback when an IMG source fails after assignment or a resolver returns no usable media.
   - Keeps latest-request-wins, Worker-first resolution, cache, gallery, District Hero Truth, and photo cost controls intact.
   - Does not change ranking, selection authority, pins, routing, Share IQ, or Place IQ content.

   V600.28 focused repair:
   - Keeps Place IQ hero hydration immediate and completely outside shelf lazy-loading.
   - Stops treating Geoapify place IDs as Google Place IDs when calling the Photo Worker.
   - Sends a generic placeId only when the candidate is explicitly Google-sourced.
   - Lets the Worker resolve Geoapify candidates by exact name + coordinates instead of a foreign ID.
   - Passes explicit hero role/source metadata through automatic Place IQ hydration.
   - Removes fallback markers as soon as a real hero image paints.
   - Preserves V600.27 three-photo prewarm, lazy shelf hydration, cache, galleries,
     Worker-first resolution, request coalescing, and never-erase behavior.

   V600.27 focused repair:
   - Reduces automatic scan photo prewarming from 12 places to the first 3.
   - Hydrates only the first 3 shelf photos immediately.
   - Defers remaining shelf photos until they approach the visible horizontal shelf viewport.
   - Uses one shared IntersectionObserver and unobserves each image after hydration begins.
   - Preserves Worker-first exact-place resolution, persistent cache, request coalescing,
     latest-request-wins, gallery support, District Hero Truth, and never-erase behavior.
   - Does not change result ranking, committed results, pins, Place IQ, or scan volume.


   V600.26 focused repair:
   - Accepts every common Photo Worker image field: heroImage, imageUrl, url,
     photoUrl, photoURL, proxyUrl, mediaUrl, thumbnail, and nested result/data payloads.
   - Accepts gallery entries using url, imageUrl, photoUrl, photoURL, proxyUrl,
     mediaUrl, heroImage, thumbnail, src, and uri.
   - Converts same-worker relative media paths into absolute HTTPS URLs.
   - Accepts valid image payloads even when the Worker omits ok:true.
   - Preserves the working V600.25 latest-request-wins, cache, gallery, and never-erase behavior.

   V600.25 focused repair:
   - Adds latest-request-wins authority to every photo element.
   - Prevents stale async photo callbacks from repainting a reused shelf or hero element.
   - Coalesces repeated shelf and Place IQ hydration requests into one scheduled pass.
   - Preserves Worker-first resolution, stable place keys, cache, gallery, and never-erase behavior.

   V600.24 focused repair:
   - Hydrates the actual current shelf photo class: .diqV600ShelfPhoto.
   - Includes that class in styling, shelf hydration, and MutationObserver detection.
   - Fixes the blank image column caused by Photo IQ watching only .diqV600ResultPhoto.
   - Preserves the V600.23 Worker-first rollback, stale-response guard, and never-erase behavior.

   V600.23 focused repair:
   - Restores the proven V600.19 Worker-first photo architecture.
   - Removes the V600.20–V600.22 provider-first, immutable-upgrade, and stable-key layers.
   - Keeps only two narrow safeguards: never erase an already-painted photo and never paint a stale response onto another place.
   - Supports both the current V600 result shelf and the legacy shelf ID.
   - Rehydrates shelf thumbnails and Place IQ heroes after render without changing resolver order.
   - Preserves District Hero Truth, Google Worker galleries, persistent cache, and business-image safety.

   Fixes:
   - Hydrates Place IQ hero images and result-shelf thumbnails automatically.
   - Reads photo URLs from deeply nested provider payloads.
   - Supports custom resolver, configured resolver endpoint, Google Places photos,
     and Wikimedia page images before using a branded fallback.
   - Prevents stale async image responses from painting the wrong place.
   - Caches successful images and removes broken cached URLs automatically.
   - Adds District Hero Truth so district cards never inherit police, sheriff,
     courthouse, fire, hospital, school, utility, office, or parking imagery.
   - Resolves district heroes using the district identity rather than a random
     member place from the cluster.
   - Rejects unsafe civic/emergency resolver matches before they reach Place IQ.
   - Uses a new district-specific cache namespace so previously cached bad district
     photos cannot survive this update.
   - Falls back to a branded destination-district visual when no trustworthy
     district image can be confirmed.
*/

(function(window,document){
"use strict";

if(window.DriveIQAdventurePhoto){return;}

var config=window.DriveIQAdventureConfig;
var utils=window.DriveIQAdventureUtils;
var state=window.DriveIQAdventureState;
var events=window.DriveIQAdventureEvents;

if(!config||!utils||!state||!events){
  throw new Error("Photo IQ requires config/utils/state/events.");
}

var memoryCache={};
var pending={};
var resolver=null;
var cacheLoaded=false;
var observer=null;
var hydrationSequence=0;
var shelfHydrationTimer=null;
var placeHydrationTimer=null;
var maximumCacheEntries=180;
var CACHE_MAX_AGE_MS=1000*60*60*24*30;

/*
  Ported from the original Adventure Mode V67 photo authority.
  The Worker resolves the exact Google Place and returns a same-origin
  image proxy URL that WKWebView can paint reliably.
*/
var DEFAULT_PHOTO_WORKER_BASE=
  "https://drive-iq-photo-resolver.loveforlocals.workers.dev";
var workerPending={};
var bundleCache={};
var MAX_GALLERY_IMAGES=5;
var SCAN_PREWARM_LIMIT=3;
var EAGER_SHELF_PHOTO_LIMIT=3;
var photoVisibilityObserver=null;

function log(){
  if(
    !config.logging||
    !config.logging.enabled||
    !window.console||
    !window.console.log
  ){return;}

  var args=Array.prototype.slice.call(arguments);
  args.unshift(config.logging.prefix,"[Photo IQ]");
  window.console.log.apply(window.console,args);
}

function warn(){
  if(!window.console||!window.console.warn){return;}
  var args=Array.prototype.slice.call(arguments);
  args.unshift(config.logging&&config.logging.prefix||"[Drive IQ]","[Photo IQ]");
  window.console.warn.apply(window.console,args);
}

function now(){
  return Date.now?Date.now():(new Date()).getTime();
}

function clean(value){
  return String(value==null?"":value).replace(/\s+/g," ").trim();
}

function norm(value){
  return clean(value).toLowerCase();
}


var DISTRICT_PHOTO_POLICY_VERSION="district-hero-v2";

var DISTRICT_UNSAFE_TERMS=[
  "police","sheriff","constable","patrol","law enforcement",
  "courthouse","court house","county jail","jail","detention",
  "fire department","fire station","fire rescue","ambulance","ems",
  "hospital","medical center","urgent care",
  "school","academy","college","university",
  "city hall","government office","county office","municipal",
  "utility","water treatment","wastewater","public works",
  "parking garage","parking lot","tow truck","impound",
  "office building","administration building"
];

var DISTRICT_PREFERRED_TERMS=[
  "waterfront","harbor","harbour","marina","boardwalk","beach",
  "skyline","streetscape","downtown","historic district","village",
  "town square","public square","landmark","outdoor dining",
  "scenic","riverfront","lakefront","oceanfront","district"
];

function entityText(item){
  return norm([
    item&&item.label,
    item&&item.name,
    item&&item.title,
    item&&item.category,
    item&&item.primaryCategory,
    item&&item.userFacingCategory,
    item&&item.poiClass,
    item&&item.entityType,
    item&&item.type,
    item&&item.description,
    item&&item.summary,
    item&&item.address,
    item&&item.full,
    item&&item.city,
    item&&item.state,
    item&&item.districtName,
    item&&item.neighborhood,
    item&&item.neighbourhood
  ].join(" "));
}

function containsTerm(textValue,terms){
  var i;

  for(i=0;i<(terms||[]).length;i+=1){
    if(textValue.indexOf(norm(terms[i]))>-1){
      return true;
    }
  }

  return false;
}

function isDistrict(item){
  var textValue=entityText(item);

  return !!(
    item&&(
      item.isDistrict===true||
      item.entityType==="district"||
      item.type==="district"||
      item.poiClass==="district"||
      item.generatedDistrict===true||
      item.districtCard===true
    )||
    /\bdistrict\b|\bdowntown\b|\bvillage\b|\btown square\b|\bneighborhood\b|\bneighbourhood\b/.test(textValue)
  );
}

function districtName(item){
  return clean(
    item&&(
      item.districtName||
      item.label||
      item.name||
      item.title||
      ""
    )||""
  );
}

function districtQueryName(item){
  var name=districtName(item);
  var city=clean(item&&item.city||"");
  var stateName=clean(item&&item.state||"");
  var text=norm(name);
  var parts=[];

  if(name){parts.push(name);}

  if(city&&text.indexOf(norm(city))<0){
    parts.push(city);
  }

  if(stateName&&text.indexOf(norm(stateName))<0){
    parts.push(stateName);
  }

  return clean(parts.join(", "));
}

function unsafeDistrictSubject(value){
  return containsTerm(norm(value),DISTRICT_UNSAFE_TERMS);
}

function unsafeDistrictItem(item){
  return isDistrict(item)&&unsafeDistrictSubject(entityText(item));
}

function preferredDistrictSignal(value){
  return containsTerm(norm(value),DISTRICT_PREFERRED_TERMS);
}

function cacheKey(value){
  var item=unwrapItem(value);
  var key=placeKey(item);

  if(!key){return "";}

  return isDistrict(item)
    ? DISTRICT_PHOTO_POLICY_VERSION+"|"+key
    : key;
}

function districtFallback(){
  return "linear-gradient(145deg,#123c52 0%,#174a52 42%,#193744 68%,#101827 100%)";
}

function unwrapItem(value){
  var item=value;

  if(item&&item.payload!==undefined){item=item.payload;}
  if(item&&item.item){item=item.item;}
  if(item&&item.place){item=item.place;}
  if(item&&item.result){item=item.result;}

  return item||null;
}

function placeKey(value){
  var item=unwrapItem(value);

  if(!item){return "";}

  return [
    item.placeId||
    item.place_id||
    item.googlePlaceId||
    item.google_place_id||
    item.id||
    item.label||
    item.name||
    "",
    item.lat!=null?item.lat:item.latitude||"",
    item.lng!=null?item.lng:item.longitude||""
  ].join("|");
}

function isBusiness(item){
  var category=categoryName(item);
  var text=norm(
    (item&&(
      item.userFacingCategory||
      item.category||
      item.primaryCategory||
      item.poiClass||
      item.type||
      ""
    ))+" "+(item&&(
      item.label||
      item.name||
      ""
    ))
  );

  return /coffee|cafe|smoothie|restaurant|food|bar|brewery|bakery|dessert|shop|store|retail|salon|hotel|business/.test(
    category+" "+text
  );
}

function unsafeBusinessImage(item,value){
  var lower=clean(value).toLowerCase();

  if(!isBusiness(item)){return false;}

  return (
    lower.indexOf("wikipedia.org")>=0||
    lower.indexOf("wikimedia.org")>=0||
    lower.indexOf("upload.wikimedia.org")>=0
  );
}

function isTrustedImageURL(value){
  var url=clean(value);
  var lower=url.toLowerCase();

  if(!url){return false;}
  if(lower.indexOf("javascript:")===0){return false;}
  if(lower.indexOf("data:text")===0){return false;}

  return (
    lower.indexOf("https://")===0||
    lower.indexOf("http://")===0||
    lower.indexOf("data:image/")===0
  );
}

function pushCandidate(list,value,item){
  if(!isTrustedImageURL(value)){return;}
  if(unsafeBusinessImage(item,value)){return;}
  value=String(value);
  if(list.indexOf(value)<0){list.push(value);}
}

function collectObjectCandidates(list,object,depth,item){
  var keys;
  var i;
  var key;
  var value;
  var lower;

  if(!object||depth>4){return;}

  if(Array.isArray(object)){
    for(i=0;i<object.length;i+=1){
      value=object[i];

      if(typeof value==="string"){
        pushCandidate(list,value,item);
      }else if(value&&typeof value==="object"){
        collectObjectCandidates(list,value,depth+1,item);
      }
    }
    return;
  }

  if(typeof object!=="object"){return;}

  keys=Object.keys(object);

  for(i=0;i<keys.length;i+=1){
    key=keys[i];
    value=object[key];
    lower=String(key).toLowerCase();

    if(
      typeof value==="string"&&
      (
        lower.indexOf("photo")>=0||
        lower.indexOf("image")>=0||
        lower.indexOf("thumbnail")>=0||
        lower==="url"||
        lower==="uri"
      )
    ){
      pushCandidate(list,value,item);
      continue;
    }

    if(
      value&&
      typeof value==="object"&&
      (
        lower.indexOf("photo")>=0||
        lower.indexOf("image")>=0||
        lower.indexOf("gallery")>=0||
        lower.indexOf("media")>=0||
        lower.indexOf("wiki")>=0||
        lower.indexOf("datasource")>=0||
        lower.indexOf("properties")>=0||
        lower.indexOf("raw")>=0
      )
    ){
      collectObjectCandidates(list,value,depth+1,item);
    }
  }
}

function candidateURLs(value){
  var item=unwrapItem(value)||{};
  var list=[];

  [
    item.diqPhotoUrl,
    item.photoURL,
    item.photoUrl,
    item.heroPhoto,
    item.heroImage,
    item.image,
    item.imageURL,
    item.imageUrl,
    item.thumbnail,
    item.thumbnailURL,
    item.thumbnailUrl,
    item.streetViewURL,
    item.streetViewUrl,
    item.mapPreviewURL,
    item.mapPreviewUrl
  ].forEach(function(url){
    pushCandidate(list,url,item);
  });

  collectObjectCandidates(list,item.gallery,0,item);
  collectObjectCandidates(list,item.photos,0,item);
  collectObjectCandidates(list,item.images,0,item);
  collectObjectCandidates(list,item.properties,0,item);
  collectObjectCandidates(list,item.datasource,0,item);
  collectObjectCandidates(list,item.raw,0,item);
  collectObjectCandidates(list,item.wiki_and_media,0,item);

  return list;
}

function categoryName(item){
  return norm(
    item&&(
      item.userFacingCategory||
      item.category||
      item.primaryCategory||
      item.poiClass||
      item.type||
      ""
    )
  );
}

function fallbackFor(item){
  var category=categoryName(item);

  if(isDistrict(item)){
    return "radial-gradient(circle at 24% 18%,rgba(85,225,255,.34),transparent 36%),"+
      districtFallback();
  }

  if(/coffee|cafe|smoothie|bakery/.test(category)){
    return "radial-gradient(circle at 24% 18%,rgba(255,224,151,.54),transparent 36%),"+
      "linear-gradient(145deg,#4a2b17 0%,#18352f 52%,#10202c 100%)";
  }

  if(/water|beach|lake|marina|river|gulf|ocean|bay|harbor|pier|boardwalk/.test(category)){
    return "radial-gradient(circle at 25% 18%,rgba(104,242,205,.42),transparent 36%),"+
      "linear-gradient(145deg,#12547a 0%,#12364f 52%,#101d31 100%)";
  }

  if(/outdoor|park|scenic|trail|nature/.test(category)){
    return "radial-gradient(circle at 24% 18%,rgba(115,238,157,.36),transparent 36%),"+
      "linear-gradient(145deg,#275f3f 0%,#173b32 55%,#101d2c 100%)";
  }

  if(/food|restaurant|burger|pizza|taco|dessert|seafood/.test(category)){
    return "radial-gradient(circle at 24% 18%,rgba(255,188,85,.50),transparent 35%),"+
      "linear-gradient(145deg,#713721 0%,#3f2727 50%,#151d2c 100%)";
  }

  if(/music|nightlife|bar|entertainment|brewery/.test(category)){
    return "radial-gradient(circle at 26% 18%,rgba(255,112,208,.38),transparent 35%),"+
      "linear-gradient(145deg,#53317d 0%,#263f67 55%,#101827 100%)";
  }

  return "radial-gradient(circle at 25% 20%,rgba(70,242,195,.34),transparent 36%),"+
    "linear-gradient(145deg,#19404f 0%,#173448 50%,#101827 100%)";
}

function fallbackMarkFor(item){
  var category=categoryName(item);

  if(isDistrict(item)){return "◆";}
  if(/coffee|cafe|smoothie|bakery/.test(category)){return "☕";}
  if(/water|beach|lake|marina|river|gulf|ocean|bay|harbor|pier|boardwalk/.test(category)){return "⌁";}
  if(/outdoor|park|scenic|trail|nature/.test(category)){return "🌿";}
  if(/food|restaurant|burger|pizza|taco|dessert|seafood/.test(category)){return "✦";}
  if(/music|nightlife|bar|entertainment|brewery/.test(category)){return "♪";}

  return "✦";
}

function paintFallbackImmediately(element,item){
  if(!element){return false;}

  /*
    A resolved photo is authoritative. Everything else may be replaced by the
    deliberate fallback while a new request is pending.
  */
  if(
    element.getAttribute("data-photo-resolved")==="1"&&
    element.classList.contains("diq-photo-ready")
  ){
    return true;
  }

  if(element.tagName==="IMG"){
    element.onerror=null;
    element.removeAttribute("src");
  }

  element.style.backgroundImage=fallbackFor(item);
  element.style.backgroundColor="#10202c";
  element.setAttribute("data-photo-source","fallback-pending");
  element.setAttribute("data-photo-fallback","1");
  element.setAttribute("data-fallback-mark",fallbackMarkFor(item));
  element.classList.add("diq-photo-fallback");
  element.classList.remove("diq-photo-ready");
  element.removeAttribute("data-photo-resolved");

  return false;
}

function ensureStyles(){
  if(document.getElementById("diqPhotoIQStyles")){return;}

  var style=document.createElement("style");
  style.id="diqPhotoIQStyles";
  style.textContent=
    "[data-diq-photo-hero],.diq-v600-place-photo,"+
    "[data-diq-result-photo],.diqV600ShelfPhoto,.diqV600ResultPhoto{"+
      "position:relative;background-color:#10202c!important;"+
      "background-position:center!important;"+
      "background-size:cover!important;"+
      "background-repeat:no-repeat!important;"+
      "transition:opacity .28s ease,filter .28s ease!important;"+
    "}"+
    ".diq-photo-loading{opacity:1;filter:saturate(.82);}"+
    ".diq-photo-ready{opacity:1;filter:none;}"+
    ".diq-photo-fallback{opacity:1;filter:none;}"+
    ".diq-photo-fallback:not(img)::after{"+
      "content:attr(data-fallback-mark);position:absolute;left:16px;bottom:14px;"+
      "z-index:2;color:rgba(255,255,255,.9);font:900 28px/1 system-ui;"+
      "text-shadow:0 8px 22px rgba(0,0,0,.42);pointer-events:none;"+
    "}"+
    "[data-diq-photo-hero].diq-photo-fallback:not(img)::after,"+
    ".diq-v600-place-photo.diq-photo-fallback:not(img)::after{"+
      "left:24px;bottom:22px;font-size:52px;"+
    "}";

  document.head.appendChild(style);
}

function loadPersistentCache(){
  var stored;

  if(cacheLoaded){return;}
  cacheLoaded=true;

  try{
    stored=utils.lsJSON(config.storage.photoCache,{});
    memoryCache=stored&&typeof stored==="object"?stored:{};
  }catch(error){
    memoryCache={};
  }
}

function trimCache(){
  var keys=Object.keys(memoryCache);

  keys.sort(function(a,b){
    return Number(memoryCache[a].cachedAt||0)-
      Number(memoryCache[b].cachedAt||0);
  });

  while(keys.length>maximumCacheEntries){
    delete memoryCache[keys.shift()];
  }
}

function persistCache(){
  trimCache();

  try{
    utils.lsSet(
      config.storage.photoCache,
      JSON.stringify(memoryCache)
    );
  }catch(error){}
}

function removeCached(item){
  var key=cacheKey(item);

  if(!key){return;}

  loadPersistentCache();
  delete memoryCache[key];
  persistCache();
}

function getCached(item){
  var key=cacheKey(item);
  var entry;

  loadPersistentCache();

  if(!key){return null;}

  entry=memoryCache[key];

  if(
    !entry||
    !isTrustedImageURL(entry.url)||
    now()-Number(entry.cachedAt||0)>CACHE_MAX_AGE_MS
  ){
    if(entry){delete memoryCache[key];}
    return null;
  }

  return {
    url:entry.url,
    source:entry.source||"cache",
    cachedAt:Number(entry.cachedAt||0),
    gallery:Array.isArray(entry.gallery)
      ? entry.gallery.slice()
      : [],
    images:Array.isArray(entry.images)
      ? entry.images.slice()
      : [],
    placeId:clean(entry.placeId||"")
  };
}

function setCached(item,url,source){
  var key=cacheKey(item);

  if(!key||!isTrustedImageURL(url)){return false;}

  loadPersistentCache();

  memoryCache[key]={
    url:String(url),
    source:String(source||"resolved"),
    cachedAt:now(),
    gallery:
      bundleCache[key]&&Array.isArray(bundleCache[key].gallery)
        ? bundleCache[key].gallery.slice(0,MAX_GALLERY_IMAGES)
        : [],
    images:
      bundleCache[key]&&Array.isArray(bundleCache[key].images)
        ? bundleCache[key].images.slice(0,MAX_GALLERY_IMAGES)
        : [],
    placeId:
      bundleCache[key]&&bundleCache[key].placeId
        ? bundleCache[key].placeId
        : ""
  };

  persistCache();
  return true;
}

function verifyImage(url,timeoutMs){
  return new Promise(function(resolve){
    var image;
    var finished=false;
    var timer;

    if(!isTrustedImageURL(url)){
      resolve(false);
      return;
    }

    image=new Image();

    function finish(valid){
      if(finished){return;}
      finished=true;

      if(timer){window.clearTimeout(timer);}

      image.onload=null;
      image.onerror=null;
      resolve(valid===true);
    }

    timer=window.setTimeout(function(){
      finish(false);
    },Number(timeoutMs||6500));

    image.onload=function(){
      finish(
        Number(image.naturalWidth||0)>=80&&
        Number(image.naturalHeight||0)>=60
      );
    };

    image.onerror=function(){
      finish(false);
    };

    image.referrerPolicy="no-referrer-when-downgrade";
    image.src=String(url);
  });
}

function resolveCandidatesSequentially(urls,index){
  var position=Number(index||0);

  if(position>=urls.length){
    return Promise.resolve(null);
  }

  return verifyImage(urls[position],6000).then(function(valid){
    if(valid){return urls[position];}
    return resolveCandidatesSequentially(urls,position+1);
  });
}

function normalizeResolverResult(result){
  if(!result){return null;}

  if(typeof result==="string"){
    return isTrustedImageURL(result)
      ? {url:result,source:"resolver"}
      : null;
  }

  if(result.url&&isTrustedImageURL(result.url)){
    return {
      url:String(result.url),
      source:String(result.source||"resolver")
    };
  }

  return null;
}

function googlePlaceId(item){
  var explicit=clean(
    item&&(
      item.googlePlaceId||
      item.google_place_id||
      item.googleId||
      ""
    )||""
  );
  var sourceText=norm(
    item&&(
      item.source||
      item.provider||
      item.photoSource||
      item.sourceProvider||
      ""
    )||""
  );
  var generic=clean(
    item&&(
      item.placeId||
      item.place_id||
      ""
    )||""
  );

  /*
    Geoapify also calls its identity field place_id. Passing that value to the
    Google Photo Worker as a Google Place ID can prevent the Worker's exact
    name/coordinate lookup from succeeding. Only trust the generic field when
    the result is explicitly Google-sourced.
  */
  if(explicit){return explicit;}

  if(
    generic&&
    /google|places api|google-places/.test(sourceText)
  ){
    return generic;
  }

  return "";
}

function photoWorkerBase(){
  var configured=clean(
    config.api&&config.api.photoResolverURL||""
  );

  if(configured){
    return configured.replace(/\/resolve\/?$/,"");
  }

  return DEFAULT_PHOTO_WORKER_BASE;
}


function absoluteWorkerImageURL(value,base){
  var raw=clean(value);
  var origin=clean(base).replace(/\/+$/,"");

  if(!raw){return "";}

  if(
    raw.indexOf("https://")===0||
    raw.indexOf("http://")===0||
    raw.indexOf("data:image/")===0
  ){
    return raw;
  }

  if(raw.indexOf("//")===0){
    return "https:"+raw;
  }

  if(raw.charAt(0)==="/"&&origin){
    return origin+raw;
  }

  if(origin&&/^(photo|image|media|proxy|places)\//i.test(raw)){
    return origin+"/"+raw.replace(/^\/+/,"");
  }

  return "";
}

function firstWorkerImageValue(data,base){
  var roots=[
    data,
    data&&data.result,
    data&&data.data,
    data&&data.payload,
    data&&data.photo,
    data&&data.image
  ];
  var keys=[
    "heroImage","heroImageUrl","hero_url",
    "imageUrl","imageURL","image_url",
    "photoUrl","photoURL","photo_url",
    "proxyUrl","proxyURL","proxy_url",
    "mediaUrl","mediaURL","media_url",
    "thumbnailUrl","thumbnailURL","thumbnail_url",
    "thumbnail","url","uri","src"
  ];
  var i;
  var j;
  var root;
  var candidate;

  for(i=0;i<roots.length;i+=1){
    root=roots[i];

    if(!root||typeof root!=="object"){continue;}

    for(j=0;j<keys.length;j+=1){
      candidate=absoluteWorkerImageURL(root[keys[j]],base);

      if(isTrustedImageURL(candidate)){
        return candidate;
      }
    }
  }

  return "";
}

function workerGalleryEntries(data){
  var roots=[
    data&&data.images,
    data&&data.gallery,
    data&&data.photos,
    data&&data.result&&data.result.images,
    data&&data.result&&data.result.gallery,
    data&&data.result&&data.result.photos,
    data&&data.data&&data.data.images,
    data&&data.data&&data.data.gallery,
    data&&data.data&&data.data.photos,
    data&&data.payload&&data.payload.images,
    data&&data.payload&&data.payload.gallery,
    data&&data.payload&&data.payload.photos
  ];
  var i;

  for(i=0;i<roots.length;i+=1){
    if(Array.isArray(roots[i])&&roots[i].length){
      return roots[i];
    }
  }

  return [];
}

function workerGalleryEntryURL(entry,base){
  var keys=[
    "url","imageUrl","imageURL","photoUrl","photoURL",
    "proxyUrl","proxyURL","mediaUrl","mediaURL",
    "heroImage","thumbnail","thumbnailUrl","src","uri"
  ];
  var i;
  var candidate;

  if(typeof entry==="string"){
    return absoluteWorkerImageURL(entry,base);
  }

  if(!entry||typeof entry!=="object"){
    return "";
  }

  for(i=0;i<keys.length;i+=1){
    candidate=absoluteWorkerImageURL(entry[keys[i]],base);

    if(isTrustedImageURL(candidate)){
      return candidate;
    }
  }

  return "";
}

function workerPhotoResolver(item){
  var base=photoWorkerBase();
  var key=cacheKey(item);
  var lat=Number(item&&(
    item.lat!=null?item.lat:item.latitude
  ));
  var lng=Number(item&&(
    item.lng!=null?item.lng:item.longitude
  ));
  var districtMode=isDistrict(item);
  var name=districtMode
    ? districtQueryName(item)
    : clean(item&&(item.label||item.name||""));
  var controller;
  var timer;

  if(
    !base||
    !name||
    !isFinite(lat)||
    !isFinite(lng)
  ){
    return Promise.resolve(null);
  }

  if(workerPending[key]){
    return workerPending[key];
  }

  controller=window.AbortController
    ? new AbortController()
    : null;

  if(controller){
    timer=window.setTimeout(function(){
      try{controller.abort();}catch(error){}
    },12000);
  }

  workerPending[key]=fetch(
    base+"/resolve",
    {
      method:"POST",
      headers:{
        "Content-Type":"application/json"
      },
      body:JSON.stringify({
        name:name,
        lat:lat,
        lng:lng,
        placeId:districtMode?"":googlePlaceId(item),
        entityType:districtMode?"district":"place",
        districtName:districtMode?districtName(item):"",
        city:clean(item&&item.city||""),
        state:clean(item&&item.state||""),
        preferredSubjects:districtMode?DISTRICT_PREFERRED_TERMS.slice():[],
        blockedSubjects:districtMode?DISTRICT_UNSAFE_TERMS.slice():[],
        gallery:true,
        maxImages:MAX_GALLERY_IMAGES
      }),
      credentials:"omit",
      cache:"no-store",
      signal:controller?controller.signal:undefined
    }
  )
  .then(function(response){
    if(!response||!response.ok){
      throw new Error(
        "Photo Worker returned "+
        (response?response.status:"no response")
      );
    }

    return response.json();
  })
  .then(function(data){
    var imageUrl=firstWorkerImageValue(data,base);
    var resolvedPlaceId=clean(
      data&&(
        data.placeId||
        data.googlePlaceId||
        data.google_place_id||
        data.result&&(
          data.result.placeId||
          data.result.googlePlaceId||
          data.result.google_place_id
        )||
        data.data&&(
          data.data.placeId||
          data.data.googlePlaceId||
          data.data.google_place_id
        )
      )||""
    );
    var resolvedIdentity=clean([
      data&&data.displayName,
      data&&data.address,
      data&&data.matchedName,
      data&&data.category,
      data&&data.types&&data.types.join
        ? data.types.join(" ")
        : ""
    ].join(" "));
    var rawImages=workerGalleryEntries(data);
    var gallery=[];
    var i;
    var entry;
    var url;
    var bundle;

    if(timer){window.clearTimeout(timer);}

    for(i=0;i<rawImages.length;i+=1){
      entry=rawImages[i];
      url=workerGalleryEntryURL(entry,base);

      if(
        isTrustedImageURL(url)&&
        gallery.indexOf(url)<0
      ){
        gallery.push(url);
      }
    }

    if(
      !imageUrl&&
      gallery.length
    ){
      imageUrl=gallery[0];
    }

    if(
      !data||
      data.ok===false||
      !isTrustedImageURL(imageUrl)
    ){
      return null;
    }

    /*
      District Hero Truth:
      The resolver may find a nearby civic entity when a generated district
      name is not an exact Google place. Never allow emergency, government,
      school, utility, medical, office, or parking identities to become the
      hero for a destination district.
    */
    if(
      districtMode&&
      (
        unsafeDistrictSubject(resolvedIdentity)||
        data.safeForDistrict===false
      )
    ){
      warn("Rejected unsafe district hero match",{
        requested:name,
        resolved:resolvedIdentity
      });
      return null;
    }

    if(
      districtMode&&
      resolvedIdentity&&
      !preferredDistrictSignal(resolvedIdentity)&&
      data.districtConfidence!=null&&
      Number(data.districtConfidence)<0.58
    ){
      warn("Rejected low-confidence district hero match",{
        requested:name,
        resolved:resolvedIdentity,
        confidence:Number(data.districtConfidence)
      });
      return null;
    }

    if(gallery.indexOf(imageUrl)<0){
      gallery.unshift(imageUrl);
    }

    if(resolvedPlaceId&&!districtMode){
      item.googlePlaceId=resolvedPlaceId;
      item.placeId=resolvedPlaceId;
    }

    bundle={
      placeId:resolvedPlaceId,
      displayName:clean(data.displayName||""),
      address:clean(data.address||""),
      heroImage:imageUrl,
      thumbnail:imageUrl,
      gallery:gallery.slice(0,MAX_GALLERY_IMAGES),
      images:rawImages.slice(0,MAX_GALLERY_IMAGES),
      photoCount:Number(data.photoCount||gallery.length||1),
      attributions:data.attributions||[],
      districtHero:districtMode,
      resolvedIdentity:resolvedIdentity,
      districtConfidence:
        data&&data.districtConfidence!=null
          ? Number(data.districtConfidence)
          : null
    };

    bundleCache[key]=bundle;

    item.workerPhotoUrl=imageUrl;
    item.photoProxyUrl=imageUrl;
    item.driveIQPhotoUrl=imageUrl;
    item.diqPhotoUrl=imageUrl;
    item.photoUrl=imageUrl;
    item.photoURL=imageUrl;
    item.thumbnail=imageUrl;
    item.thumbnailURL=imageUrl;
    item.heroPhoto=imageUrl;
    item.heroImage=imageUrl;
    item.gallery=bundle.gallery.slice();
    item.photos=bundle.gallery.slice();
    item.photoImages=bundle.images.slice();
    item.photoCount=bundle.photoCount;
    item.photoSource=districtMode
      ? "Drive IQ District Hero Truth V2"
      : "Drive IQ Google Photo Worker V3 Gallery";

    if(bundle.attributions){
      item.photoAttributions=bundle.attributions;
    }

    return {
      url:imageUrl,
      source:districtMode
        ? "drive-iq-district-hero-truth-v2"
        : "drive-iq-google-photo-worker-v3-gallery",
      gallery:bundle.gallery.slice(),
      images:bundle.images.slice(),
      photoCount:bundle.photoCount,
      bundle:bundle
    };
  })
  .catch(function(error){
    if(timer){window.clearTimeout(timer);}
    warn("Google Photo Worker failed",error);
    return null;
  })
  .then(function(result){
    delete workerPending[key];
    return result;
  });

  return workerPending[key];
}

function configuredResolver(item){
  var endpoint=clean(
    config.api&&config.api.photoResolverURL||""
  );

  if(
    !endpoint||
    endpoint.indexOf(DEFAULT_PHOTO_WORKER_BASE)===0
  ){
    return Promise.resolve(null);
  }

  return fetch(endpoint,{
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({
      placeId:item.placeId||
        item.place_id||
        item.googlePlaceId||
        "",
      name:item.label||item.name||"",
      address:item.address||
        item.formattedAddress||
        item.full||
        "",
      lat:Number(item.lat||item.latitude),
      lng:Number(item.lng||item.longitude),
      category:item.category||
        item.primaryCategory||
        "",
      entityType:isDistrict(item)?"district":"place",
      districtName:isDistrict(item)?districtName(item):"",
      preferredSubjects:isDistrict(item)?DISTRICT_PREFERRED_TERMS.slice():[],
      blockedSubjects:isDistrict(item)?DISTRICT_UNSAFE_TERMS.slice():[]
    })
  })
  .then(function(response){
    if(!response.ok){
      throw new Error("Photo resolver returned "+response.status);
    }
    return response.json();
  })
  .then(function(result){
    var normalized=normalizeResolverResult(result);

    if(
      normalized&&
      isDistrict(item)&&
      (
        unsafeDistrictSubject(
          clean(
            result&&(
              result.displayName||
              result.name||
              result.address||
              result.category||
              ""
            )
          )
        )||
        result&&result.safeForDistrict===false
      )
    ){
      return null;
    }

    return normalized;
  })
  .catch(function(error){
    warn("Configured resolver failed",error);
    return null;
  });
}

function googlePhotoResolver(item){
  if(isDistrict(item)){return Promise.resolve(null);}

  var key=clean(
    config.api&&config.api.googlePlacesKey||""
  );
  var placeId=clean(
    item.googlePlaceId||
    item.google_place_id||
    ""
  );

  if(!key||!placeId){return Promise.resolve(null);}

  var detailsURL=
    "https://maps.googleapis.com/maps/api/place/details/json"+
    "?place_id="+encodeURIComponent(placeId)+
    "&fields=photos"+
    "&key="+encodeURIComponent(key);

  return fetch(detailsURL)
    .then(function(response){
      if(!response.ok){throw new Error("Google details failed");}
      return response.json();
    })
    .then(function(data){
      var photo=
        data&&data.result&&
        data.result.photos&&
        data.result.photos[0];

      if(!photo||!photo.photo_reference){return null;}

      return {
        url:
          "https://maps.googleapis.com/maps/api/place/photo"+
          "?maxwidth=1400"+
          "&photo_reference="+encodeURIComponent(photo.photo_reference)+
          "&key="+encodeURIComponent(key),
        source:"google-places"
      };
    })
    .catch(function(){
      return null;
    });
}

function wikimediaResolver(item){
  if(isDistrict(item)){return Promise.resolve(null);}

  var name=clean(item.label||item.name||"");
  var city=clean(item.city||"");
  var stateName=clean(item.state||"");
  var query=[name,city,stateName].filter(Boolean).join(" ");

  if(!name){return Promise.resolve(null);}

  var url=
    "https://en.wikipedia.org/w/api.php"+
    "?origin=*"+
    "&action=query"+
    "&generator=search"+
    "&gsrsearch="+encodeURIComponent(query)+
    "&gsrlimit=4"+
    "&prop=pageimages"+
    "&piprop=original|thumbnail"+
    "&pithumbsize=1200"+
    "&format=json";

  return fetch(url,{
    method:"GET",
    mode:"cors",
    credentials:"omit"
  })
  .then(function(response){
    if(!response.ok){throw new Error("Wikimedia search failed");}
    return response.json();
  })
  .then(function(data){
    var pages=data&&data.query&&data.query.pages
      ? Object.keys(data.query.pages).map(function(key){
          return data.query.pages[key];
        })
      : [];
    var page;
    var i;

    for(i=0;i<pages.length;i+=1){
      page=pages[i];

      if(page.original&&isTrustedImageURL(page.original.source)){
        return {
          url:page.original.source,
          source:"wikimedia"
        };
      }

      if(page.thumbnail&&isTrustedImageURL(page.thumbnail.source)){
        return {
          url:page.thumbnail.source,
          source:"wikimedia"
        };
      }
    }

    return null;
  })
  .catch(function(){
    return null;
  });
}

function resolveExternal(item,options){
  var supplied=options||{};

  return Promise.resolve(
    resolver?resolver(item,supplied):null
  )
  .then(normalizeResolverResult)
  .then(function(result){
    if(result){return result;}
    return workerPhotoResolver(item);
  })
  .then(function(result){
    if(result){return result;}
    return configuredResolver(item);
  })
  .then(function(result){
    if(result){return result;}
    return googlePhotoResolver(item);
  })
  .then(function(result){
    if(result){return result;}

    /*
      Wikimedia is never allowed for consumer businesses. It can return
      a person or unrelated article image for a venue name.
    */
    if(isBusiness(item)){return null;}

    return wikimediaResolver(item);
  });
}

function resolve(value,options){
  var item=unwrapItem(value);
  var supplied=options||{};
  var key=cacheKey(item);
  var cached;
  var candidates;

  if(!item||!key){
    return Promise.resolve({
      url:"",
      source:"none",
      fallback:fallbackFor(item)
    });
  }

  if(unsafeDistrictItem(item)){
    return Promise.resolve({
      url:"",
      source:"district-unsafe-fallback",
      fallback:districtFallback()
    });
  }

  cached=supplied.ignoreCache===true?null:getCached(item);

  if(cached){
    return verifyImage(cached.url,5000).then(function(valid){
      if(valid){
        if(cached.placeId){
          item.googlePlaceId=cached.placeId;
          item.placeId=cached.placeId;
        }

        if(cached.gallery&&cached.gallery.length){
          item.gallery=cached.gallery.slice();
          item.photos=cached.gallery.slice();
          item.thumbnail=cached.gallery[0];
          item.heroImage=cached.gallery[0];
        }

        return {
          url:cached.url,
          source:cached.source,
          cached:true,
          gallery:cached.gallery||[],
          images:cached.images||[],
          fallback:fallbackFor(item)
        };
      }

      removeCached(item);
      return resolve(item,{ignoreCache:true});
    });
  }

  if(pending[key]){return pending[key];}

  candidates=isDistrict(item)
    ? []
    : candidateURLs(item);

  /*
    Original Adventure authority: resolve the exact Google business photo
    through the Drive IQ Worker first. Raw provider URLs are fallback only.
  */
  pending[key]=resolveExternal(item,supplied)
    .then(function(externalResult){
      if(externalResult&&externalResult.url){
        return externalResult;
      }

      return resolveCandidatesSequentially(candidates,0)
        .then(function(candidateURL){
          if(!candidateURL){return null;}

          return {
            url:candidateURL,
            source:"provider-item",
            cached:false,
            fallback:fallbackFor(item)
          };
        });
    })
    .then(function(result){
      if(!result||!result.url){return null;}

      return verifyImage(result.url,7000).then(function(valid){
        if(!valid){return null;}

        setCached(item,result.url,result.source);

        return {
          url:result.url,
          source:result.source,
          cached:false,
          gallery:Array.isArray(result.gallery)
            ? result.gallery.slice()
            : (
              bundleCache[key]&&Array.isArray(bundleCache[key].gallery)
                ? bundleCache[key].gallery.slice()
                : []
            ),
          images:Array.isArray(result.images)
            ? result.images.slice()
            : (
              bundleCache[key]&&Array.isArray(bundleCache[key].images)
                ? bundleCache[key].images.slice()
                : []
            ),
          photoCount:Number(
            result.photoCount||
            (
              bundleCache[key]&&
              bundleCache[key].photoCount
            )||
            0
          ),
          fallback:fallbackFor(item)
        };
      });
    })
    .then(function(result){
      var finalResult=result||{
        url:"",
        source:"fallback",
        cached:false,
        fallback:fallbackFor(item)
      };

      events.emit("photo:resolved",{
        item:item,
        key:key,
        result:finalResult
      });

      return finalResult;
    })
    .catch(function(error){
      var fallbackResult={
        url:"",
        source:"error",
        error:clean(error&&error.message||error||"Photo failed"),
        fallback:fallbackFor(item)
      };

      events.emit("photo:failed",{
        item:item,
        key:key,
        result:fallbackResult
      });

      return fallbackResult;
    })
    .then(function(result){
      delete pending[key];
      return result;
    });

  return pending[key];
}

function applyToElement(element,result,item){
  var fallbackResult=result||{};
  var imageURL=clean(fallbackResult.url||"");

  if(!element){return false;}

  element.classList.remove("diq-photo-loading");

  if(imageURL){
    if(element.tagName==="IMG"){
      /*
        Keep the branded background behind the IMG. If the assigned source
        fails after verification, the element falls back cleanly instead of
        exposing a black or broken image slot.
      */
      element.onerror=function(){
        element.onerror=null;
        element.removeAttribute("src");
        element.removeAttribute("data-photo-resolved");
        element.classList.remove("diq-photo-ready");
        element.style.backgroundImage=fallbackFor(item);
        element.style.backgroundColor="#10202c";
        element.setAttribute("data-photo-source","fallback-after-img-error");
        element.setAttribute("data-photo-fallback","1");
        element.setAttribute("data-fallback-mark",fallbackMarkFor(item));
        element.classList.add("diq-photo-fallback");
      };
      element.style.backgroundImage=fallbackFor(item);
      element.src=imageURL;
    }else{
      element.style.backgroundImage=
        'url("'+String(imageURL).replace(/"/g,'\\"')+'")';
    }

    element.setAttribute(
      "data-photo-source",
      fallbackResult.source||"resolved"
    );
    element.classList.add("diq-photo-ready");
    element.classList.remove("diq-photo-fallback");
    element.removeAttribute("data-photo-fallback");
    element.removeAttribute("data-fallback-mark");
    element.setAttribute("data-photo-resolved","1");
    return true;
  }

  /*
    Never-Erase Guard applies only to a confirmed real photo. A deliberate
    fallback is not treated as resolved media and therefore cannot block a
    later successful hydration.
  */
  if(
    element.getAttribute("data-photo-resolved")==="1"&&
    element.classList.contains("diq-photo-ready")
  ){
    return true;
  }

  if(element.tagName==="IMG"){
    element.onerror=null;
    element.removeAttribute("src");
  }

  element.style.backgroundImage=
    fallbackResult.fallback||fallbackFor(item);
  element.style.backgroundColor="#10202c";
  element.setAttribute("data-photo-source",fallbackResult.source||"fallback");
  element.setAttribute("data-photo-fallback","1");
  element.setAttribute("data-fallback-mark",fallbackMarkFor(item));
  element.classList.add("diq-photo-fallback");
  element.classList.remove("diq-photo-ready");
  element.removeAttribute("data-photo-resolved");
  return false;
}

function hydrateElement(value,element,options){
  var item=unwrapItem(value);
  var key=placeKey(item);
  var requestId;

  if(!element||!item||!key){
    return Promise.resolve(null);
  }

  hydrationSequence+=1;
  requestId=String(hydrationSequence);

  element.setAttribute("data-photo-place-key",key);
  element.setAttribute("data-diq-photo-place-key",key);
  element.setAttribute("data-diq-photo-request-id",requestId);

  /*
    Media Fallback Authority:
    paint first, then resolve. The user should always see an intentional visual
    rather than a black loading square.
  */
  paintFallbackImmediately(element,item);
  element.classList.add("diq-photo-loading");

  return resolve(item,options).then(function(result){
    /*
      V600.25 Latest Hydration Authority:
      the element may be reused or rehydrated while an older async request is
      still resolving. Only the newest request for this exact element may paint.
    */
    if(
      element.getAttribute("data-photo-place-key")!==key||
      element.getAttribute("data-diq-photo-place-key")!==key||
      element.getAttribute("data-diq-photo-request-id")!==requestId
    ){
      return result;
    }

    applyToElement(element,result,item);

    events.emit("photo:applied",{
      item:item,
      element:element,
      result:result,
      requestId:requestId
    });

    return result;
  });
}

function currentPlaceItem(){
  var item=null;

  try{
    if(
      window.DriveIQAdventurePlaceCard&&
      typeof window.DriveIQAdventurePlaceCard.current==="function"
    ){
      item=window.DriveIQAdventurePlaceCard.current();
    }
  }catch(error){}

  if(!item){
    item=state.get("placeCard.item",null)||
      state.get("selectedPlace",null);
  }

  return unwrapItem(item);
}

function hydrateCurrentPlaceCard(value){
  var item=unwrapItem(value)||currentPlaceItem();
  var card=document.getElementById(config.placeCard.cardID);
  var hero;

  if(!item||!card){return Promise.resolve(null);}

  hero=
    card.querySelector("[data-diq-photo-hero]")||
    card.querySelector(".diqPlaceHero")||
    card.querySelector(".diq-v600-place-photo")||
    card.querySelector("img");

  if(!hero){return Promise.resolve(null);}

  return hydrateElement(item,hero,{
    source:"place-card-hero-auto",
    role:"hero",
    immediate:true
  });
}

function resultItems(){
  return state.get("results.items",[])||[];
}

function findResultItem(element,index){
  var list=resultItems();
  var key=element&&element.getAttribute(
    "data-photo-place-key"
  );
  var item=null;

  if(key){
    list.some(function(candidate){
      if(placeKey(candidate)===key){
        item=candidate;
        return true;
      }
      return false;
    });
  }

  if(!item&&isFinite(Number(index))){
    item=list[Number(index)]||null;
  }

  return item;
}

function ensurePhotoVisibilityObserver(shelf){
  if(photoVisibilityObserver){return photoVisibilityObserver;}
  if(!window.IntersectionObserver){return null;}

  photoVisibilityObserver=new IntersectionObserver(function(entries){
    entries.forEach(function(entry){
      var element=entry&&entry.target;
      var item;

      if(!element||(!entry.isIntersecting&&Number(entry.intersectionRatio||0)<=0)){
        return;
      }

      try{photoVisibilityObserver.unobserve(element);}catch(error){}

      item=findResultItem(
        element,
        Number(element.getAttribute("data-diq-photo-result-index"))
      );

      if(item){
        hydrateElement(item,element);
      }
    });
  },{
    root:shelf||null,
    rootMargin:"0px 260px 0px 260px",
    threshold:0.01
  });

  return photoVisibilityObserver;
}

function hydrateResultShelf(){
  var shelf=
    document.getElementById("diqV600ResultShelf")||
    document.getElementById("diqAdventureResultsShelf");
  var elements;
  var tasks=[];
  var visibilityObserver;

  if(!shelf){return Promise.resolve([]);}

  visibilityObserver=ensurePhotoVisibilityObserver(shelf);

  elements=shelf.querySelectorAll(
    "[data-diq-result-photo],"+
    ".diqV600ShelfPhoto,"+
    ".diqV600ResultPhoto,"+
    ".diqResultPhoto,"+
    ".diqResultThumb"
  );

  Array.prototype.forEach.call(elements,function(element,index){
    var item=findResultItem(element,index);

    if(!item){return;}

    element.setAttribute("data-diq-photo-result-index",String(index));
    element.setAttribute("data-photo-place-key",placeKey(item));
    element.setAttribute("data-diq-photo-place-key",placeKey(item));

    /*
      Deferred thumbnails still receive a complete branded visual immediately.
      IntersectionObserver controls only the remote request, never whether the
      shelf looks finished.
    */
    paintFallbackImmediately(element,item);

    if(
      index<EAGER_SHELF_PHOTO_LIMIT||
      !visibilityObserver||
      element.getAttribute("data-photo-resolved")==="1"
    ){
      tasks.push(hydrateElement(item,element));
      return;
    }

    visibilityObserver.observe(element);
  });

  return Promise.all(tasks);
}

function prewarm(items,limit){
  var list=Array.isArray(items)?items:[];
  var max=Math.min(list.length,Number(limit||10));

  list.slice(0,max).forEach(function(item){
    resolve(item).catch(function(){});
  });
}

function scheduleShelfHydration(delay){
  if(shelfHydrationTimer){
    window.clearTimeout(shelfHydrationTimer);
  }

  shelfHydrationTimer=window.setTimeout(function(){
    shelfHydrationTimer=null;
    hydrateResultShelf();
  },Math.max(0,Number(delay||0)));

  return true;
}

function schedulePlaceHydration(value,delay){
  var item=unwrapItem(value);

  if(placeHydrationTimer){
    window.clearTimeout(placeHydrationTimer);
  }

  placeHydrationTimer=window.setTimeout(function(){
    placeHydrationTimer=null;
    hydrateCurrentPlaceCard(item);
  },Math.max(0,Number(delay||0)));

  return true;
}

function installObserver(){
  if(observer||!window.MutationObserver){return;}

  observer=new MutationObserver(function(mutations){
    var placeChanged=false;
    var shelfChanged=false;

    mutations.forEach(function(mutation){
      Array.prototype.forEach.call(
        mutation.addedNodes||[],
        function(node){
          if(!node||node.nodeType!==1){return;}

          if(
            node.id===config.placeCard.cardID||
            (
              node.querySelector&&
              node.querySelector("[data-diq-photo-hero],.diq-v600-place-photo")
            )
          ){
            placeChanged=true;
          }

          if(
            node.id==="diqV600ResultShelf"||
            node.id==="diqAdventureResultsShelf"||
            (
              node.querySelector&&
              node.querySelector("[data-diq-result-photo],.diqV600ShelfPhoto,.diqV600ResultPhoto")
            )
          ){
            shelfChanged=true;
          }
        }
      );
    });

    if(placeChanged){
      schedulePlaceHydration(null,0);
    }

    if(shelfChanged){
      scheduleShelfHydration(0);
    }
  });

  observer.observe(document.body||document.documentElement,{
    childList:true,
    subtree:true
  });
}

function registerResolver(fn){
  if(fn!==null&&typeof fn!=="function"){
    throw new Error("Photo resolver must be a function or null.");
  }

  resolver=fn;

  return function unregisterResolver(){
    if(resolver===fn){resolver=null;}
  };
}

function photoBundle(value){
  var item=unwrapItem(value);
  var key=cacheKey(item);
  var cached=getCached(item);
  var bundle=bundleCache[key]||null;

  if(bundle){
    return {
      placeId:bundle.placeId||googlePlaceId(item),
      heroImage:bundle.heroImage||"",
      thumbnail:bundle.thumbnail||bundle.heroImage||"",
      gallery:Array.isArray(bundle.gallery)
        ? bundle.gallery.slice()
        : [],
      images:Array.isArray(bundle.images)
        ? bundle.images.slice()
        : [],
      photoCount:Number(bundle.photoCount||0),
      attributions:bundle.attributions||[]
    };
  }

  if(cached){
    return {
      placeId:cached.placeId||googlePlaceId(item),
      heroImage:cached.url||"",
      thumbnail:cached.url||"",
      gallery:cached.gallery&&cached.gallery.length
        ? cached.gallery.slice()
        : [cached.url],
      images:cached.images||[],
      photoCount:
        cached.gallery&&cached.gallery.length
          ? cached.gallery.length
          : 1,
      attributions:item&&item.photoAttributions||[]
    };
  }

  return null;
}

function galleryURL(value,index){
  var bundle=photoBundle(value);
  var position=Math.max(0,Number(index||0));

  if(
    !bundle||
    !bundle.gallery||
    !bundle.gallery.length
  ){
    return "";
  }

  return bundle.gallery[position]||
    bundle.gallery[0]||
    "";
}

function clearCache(){
  memoryCache={};
  pending={};
  bundleCache={};

  try{
    utils.lsRemove(config.storage.photoCache);
  }catch(error){}

  events.emit("photo:cache-cleared",{});
  return true;
}

function syncExternalResultsPillForPlaceMode(){
  var pill=
    document.getElementById("diqV600ResultsPill")||
    document.querySelector("[data-diq-results-pill]");
  var placeOpen=!!(
    state.get("ui.mode","")==="place"||
    (
      document.body&&
      (
        document.body.classList.contains("diq-v600-place")||
        document.body.classList.contains("diqPlaceIQMode")
      )
    )
  );

  if(!pill){return false;}

  if(placeOpen){
    pill.classList.remove("on");
    pill.setAttribute("aria-hidden","true");
    pill.style.pointerEvents="none";
  }

  return placeOpen;
}

function init(){
  ensureStyles();
  loadPersistentCache();
  installObserver();

  scheduleShelfHydration(100);
  schedulePlaceHydration(null,100);

  return true;
}

events.on("place:opened",function(event){
  var item=unwrapItem(event);

  syncExternalResultsPillForPlaceMode();

  syncExternalResultsPillForPlaceMode();
  schedulePlaceHydration(item,0);
});

events.on("scan:completed",function(event){
  var payload=event&&event.payload?event.payload:event||{};
  var items=payload.results||resultItems();

  prewarm(items,SCAN_PREWARM_LIMIT);

  scheduleShelfHydration(40);
});

events.on("results:rendered",function(){
  syncExternalResultsPillForPlaceMode();
  syncExternalResultsPillForPlaceMode();
  scheduleShelfHydration(0);
});

window.DriveIQAdventurePhoto={
  init:init,
  resolve:resolve,
  hydrateElement:hydrateElement,
  hydrateResults:function(items,elementResolver){
    var list=Array.isArray(items)?items:[];
    var tasks=[];

    list.forEach(function(item,index){
      var element=typeof elementResolver==="function"
        ? elementResolver(item,index)
        : null;

      if(element){
        tasks.push(hydrateElement(item,element));
      }
    });

    return Promise.all(tasks);
  },
  hydrateCurrentPlaceCard:hydrateCurrentPlaceCard,
  hydrateResultShelf:hydrateResultShelf,
  applyToElement:applyToElement,
  registerResolver:registerResolver,
  getCached:getCached,
  setCached:setCached,
  clearCache:clearCache,
  placeKey:placeKey,
  fallbackFor:fallbackFor,
  candidateURLs:candidateURLs,
  photoBundle:photoBundle,
  galleryURL:galleryURL,
  prewarm:prewarm,
  isDistrict:isDistrict,
  districtName:districtName,
  districtQueryName:districtQueryName,
  unsafeDistrictSubject:unsafeDistrictSubject,
  districtFallback:districtFallback,
  syncExternalResultsPillForPlaceMode:syncExternalResultsPillForPlaceMode,
  status:function(){
    return {
      build:"V600_28_PLACE_IQ_HERO_RESCUE",
      cacheEntries:Object.keys(memoryCache).length,
      pending:Object.keys(pending).length,
      hasCustomResolver:typeof resolver==="function",
      hasConfiguredResolver:!!clean(
        config.api&&config.api.photoResolverURL||""
      ),
      hasGoogleKey:!!clean(
        config.api&&config.api.googlePlacesKey||""
      ),
      photoWorkerBase:photoWorkerBase(),
      workerPending:Object.keys(workerPending).length,
      galleryBundles:Object.keys(bundleCache).length,
      maxGalleryImages:MAX_GALLERY_IMAGES,
      scanPrewarmLimit:SCAN_PREWARM_LIMIT,
      eagerShelfPhotoLimit:EAGER_SHELF_PHOTO_LIMIT,
      placeIQHeroImmediate:true,
      geoapifyPlaceIdIsolation:true,
      lazyShelfHydration:!!window.IntersectionObserver,
      workerFirst:true,
      latestHydrationWins:true,
      scheduledHydration:true,
      hydrationSequence:hydrationSequence,
      galleryIQ:true,
      wikimediaBlockedForBusinesses:true,
      districtHeroTruth:true,
      districtPhotoPolicyVersion:DISTRICT_PHOTO_POLICY_VERSION,
      districtUnsafeTermCount:DISTRICT_UNSAFE_TERMS.length,
      districtPreferredTermCount:DISTRICT_PREFERRED_TERMS.length
    };
  }
};

if(document.readyState==="loading"){
  document.addEventListener("DOMContentLoaded",init,{once:true});
}else{
  init();
}

log("V600.28 Place IQ Hero Rescue loaded");

})(window,document);
